import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, ButtonGroup, Card, Form, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Collaboration, { isChangeOrigin } from "@tiptap/extension-collaboration";
import { CollaborationCaret } from "@tiptap/extension-collaboration-caret";
import { HocuspocusProvider } from "@hocuspocus/provider";
import * as Y from "yjs";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { v4 as uuidv4 } from "uuid";
import {
  addAgreementLibraryProvision,
  approveAgreementLibraryVersion,
  customizeAgreementProvision,
  downloadAgreementArtifact,
  finalizeAgreement,
  getAgreement,
  getAgreementArtifacts,
  getAgreementAudit,
  getAgreementContext,
  getAgreementLibrary,
  getAgreementTypes,
  getAgreementVersions,
  updateAgreement,
} from "../../../../actions/agreement";
import { agreementExtensions } from "./extensions";
import "./styles.css";
import "./history.css";

const STYLE_CYCLE = ["decimal", "lower-alpha", "lower-roman"];
const REFERENCE_STYLES = ["paragraph", "section", "provision", "article", "bare"];

function json(value, fallback) {
  if (value == null || value === "") return fallback;
  if (typeof value === "object") return value;
  try { return JSON.parse(value); } catch (_error) { return fallback; }
}

function alpha(value) {
  let number = value;
  let result = "";
  while (number > 0) { number -= 1; result = String.fromCharCode(97 + number % 26) + result; number = Math.floor(number / 26); }
  return result || "a";
}

function roman(value) {
  let number = value;
  let result = "";
  for (const [amount, glyph] of [[1000,"m"],[900,"cm"],[500,"d"],[400,"cd"],[100,"c"],[90,"xc"],[50,"l"],[40,"xl"],[10,"x"],[9,"ix"],[5,"v"],[4,"iv"],[1,"i"]]) {
    while (number >= amount) { result += glyph; number -= amount; }
  }
  return result || "i";
}

function segment(value, style) {
  if (style === "lower-alpha") return alpha(value);
  if (style === "lower-roman") return roman(value);
  return String(value);
}

function numberedTree(items = [], config = {}) {
  const source = items.map((item) => ({ ...item, id: item.id || item.provisionId, parentId: item.parentId || item.parentProvisionId || null }));
  const children = new Map([[null, []]]);
  source.forEach((item) => { if (!children.has(item.parentId)) children.set(item.parentId, []); children.get(item.parentId).push(item); });
  children.forEach((siblings) => siblings.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)));
  const result = [];
  const byId = {};
  const walk = (parentId, parentSegments = [], level = 0) => {
    (children.get(parentId) || []).forEach((item, index) => {
      const style = item.numberingStyle || config.levelStyles?.[level] || STYLE_CYCLE[level % STYLE_CYCLE.length];
      const segments = [...parentSegments, segment(index + 1, style)];
      const resolved = { ...item, level, numberingStyle: style, displayNumber: `${segments.join(".")}${level === 0 ? "." : ""}`, segments };
      result.push(resolved);
      byId[resolved.id] = resolved;
      walk(resolved.id, segments, level + 1);
    });
  };
  walk(null);
  return { items: result, byId };
}

function referenceText(target, style = "paragraph", includeTitle = false) {
  if (!target) return "Unresolved reference";
  const prefix = style === "bare" ? "" : style === "article" ? "Article " : `${style.charAt(0).toUpperCase()}${style.slice(1)} `;
  const number = style === "article" ? roman(Number(target.segments?.[0] || 1)).toUpperCase() : target.displayNumber;
  return `${prefix}${number}${includeTitle && target.title ? ` (${target.title})` : ""}`;
}

function decorateDocument(document, tree, numberingConfig) {
  const numbered = numberedTree(tree, numberingConfig);
  const nodes = new Map((document?.content || []).filter((node) => node.type === "agreementProvision").map((node) => [node.attrs?.provisionId, node]));
  const visit = (node) => {
    if (node.type === "provisionReference") {
      const target = numbered.byId[node.attrs?.targetProvisionId];
      return { ...node, attrs: { ...node.attrs, resolved: Boolean(target), resolvedText: referenceText(target, node.attrs?.referenceStyle, node.attrs?.includeTitle) } };
    }
    return { ...node, ...(Array.isArray(node.content) ? { content: node.content.map(visit) } : {}) };
  };
  const provisions = numbered.items.map((item) => {
    const existing = nodes.get(item.id) || { type: "agreementProvision", content: [{ type: "paragraph" }] };
    return visit({ ...existing, attrs: { ...(existing.attrs || {}), provisionId: item.id, parentProvisionId: item.parentId, categoryId: item.categoryId || item.categoryUuid, level: item.level, sortOrder: item.sortOrder, numberingStyle: item.numberingStyle, displayNumber: item.displayNumber, status: item.status || "draft" } });
  });
  const source = document?.content || [];
  const firstProvision = source.findIndex((node) => node.type === "agreementProvision");
  const before = firstProvision < 0 ? source.filter((node) => node.type !== "agreementProvision") : source.slice(0, firstProvision).filter((node) => node.type !== "agreementProvision");
  const after = firstProvision < 0 ? [] : source.slice(firstProvision).filter((node) => node.type !== "agreementProvision");
  return { type: "doc", content: [...before.map(visit), ...provisions, ...after.map(visit)] };
}

function templateText(node) {
  if (!node) return "";
  if (node.type === "text") return node.text || "";
  if (node.type === "mergeField") return `[[${node.attrs?.key || "unresolved"}${node.attrs?.format ? `|${node.attrs.format}` : ""}]]`;
  if (node.type === "provisionReference") return `[[provisionReference:${node.attrs?.targetProvisionId || "unresolved"}|${node.attrs?.referenceStyle || "paragraph"}|${node.attrs?.includeTitle ? "title" : "number"}]]`;
  const value = (node.content || []).map(templateText).join("");
  return ["paragraph", "heading"].includes(node.type) ? `${value}\n` : value;
}

function collaborationUrl() {
  const raw = String(localStorage.api || window.location.origin).replace(/^['"]+|['";]+$/g, "");
  const base = new URL(raw.startsWith("/") ? raw : (/^https?:\/\//i.test(raw) ? raw : `https://${raw}`), window.location.origin);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  base.pathname = "/api/v1/agreements/collaboration";
  base.search = new URLSearchParams({ instance: localStorage.instance }).toString();
  return base.toString();
}

function errorMessage(error) {
  const payload = error?.response?.data?.error;
  const validation = payload?.errors || error?.response?.data?.errors;
  if (Array.isArray(validation) && validation.length) return validation.map((item) => item.message || item.code).join(" ");
  return payload?.message || error?.response?.data?.message || error?.message || "The agreement request failed.";
}

function collaborationHistoryManager(editor) {
  if (!editor?.state?.plugins) return null;
  for (const plugin of editor.state.plugins) {
    const state = plugin.getState(editor.state);
    if (state?.undoManager?.undoStack && state?.undoManager?.redoStack) return state.undoManager;
  }
  return null;
}

function AgreementToolbarButton({ active = false, disabled = false, title, onClick, children }) {
  return (
    <Button
      type="button"
      variant={active ? "primary" : "light"}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function SortableProvision({ item, active, onSelect, onIndent, onOutdent, onRemove, readOnly }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: readOnly });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, marginLeft: item.level * 12 }} className={`agreement-outline-item${active ? " is-active" : ""}${isDragging ? " is-dragging" : ""}`}>
      <button type="button" className="agreement-drag" {...attributes} {...listeners} aria-label={`Move ${item.title || "provision"}`}>⋮⋮</button>
      <button type="button" className="agreement-outline-label" onClick={() => onSelect(item.id)}>
        <span>{item.displayNumber}</span><strong>{item.title || "Untitled provision"}</strong>
      </button>
      {!readOnly ? <span className="agreement-nest-actions"><button type="button" onClick={() => onOutdent(item.id)} aria-label="Outdent">‹</button><button type="button" onClick={() => onIndent(item.id)} aria-label="Indent">›</button><button type="button" onClick={() => onRemove(item.id)} aria-label="Remove">×</button></span> : null}
    </div>
  );
}

function Preview({ content }) {
  const editor = useEditor({ extensions: [StarterKit, Underline, TextAlign.configure({ types: ["heading", "paragraph"] }), ...agreementExtensions], content: content || { type: "doc", content: [] }, editable: false });
  useEffect(() => { if (editor && content) editor.commands.setContent(content); }, [editor, content]);
  return <EditorContent editor={editor} className="agreement-paper is-preview" />;
}

const AgreementWorkspace = forwardRef(function AgreementWorkspace({ agreement: initialAgreement, matter, user, permissions, onAgreementChange, onDirtyChange }, ref) {
  const [agreement, setAgreement] = useState(initialAgreement);
  const [mode, setMode] = useState("template");
  const [connection, setConnection] = useState("connecting");
  const [synced, setSynced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [showFinalize, setShowFinalize] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mergeContext, setMergeContext] = useState({ aliases: {} });
  const [validation, setValidation] = useState({ unresolvedFields: [], unresolvedReferences: [] });
  const [versions, setVersions] = useState([]);
  const [audits, setAudits] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [tombstones, setTombstones] = useState({});
  const [library, setLibrary] = useState([]);
  const [activeProvisionId, setActiveProvisionId] = useState(initialAgreement.provisionTree?.[0]?.id || null);
  const [activeCategoryUuid, setActiveCategoryUuid] = useState(initialAgreement.provisionTree?.[0]?.categoryId || null);
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiOptions, setAiOptions] = useState([]);
  const [aiHistory, setAiHistory] = useState([]);
  const [draftingControls, setDraftingControls] = useState([]);
  const [variableValues, setVariableValues] = useState({});
  const [referenceTarget, setReferenceTarget] = useState(null);
  const [referenceStyle, setReferenceStyle] = useState({ value: "paragraph", label: "Paragraph" });
  const [mergeField, setMergeField] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false });
  const dirtyRef = useRef(false);
  const hydratedRef = useRef(false);
  const canEdit = permissions.edit && !agreement.collaboration?.readOnly;
  const plan = json(agreement.AgreementType?.provisionPlan || agreement.provisionPlan, []);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const markDirty = useCallback((value = true) => {
    const next = Boolean(value);
    dirtyRef.current = next;
    setDirty(next);
  }, []);

  useEffect(() => {
    onDirtyChange?.(agreement.uuid, dirty);
  }, [agreement.uuid, dirty, onDirtyChange]);

  const collaboration = useMemo(() => {
    const document = new Y.Doc();
    const provider = new HocuspocusProvider({
      url: collaborationUrl(),
      name: initialAgreement.collaboration.documentKey,
      document,
      token: () => localStorage.token,
      sessionAwareness: true,
      onStatus: ({ status }) => setConnection(status),
      onSynced: ({ state }) => setSynced(state),
      onAuthenticationFailed: () => setMessage({ variant: "danger", text: "The collaboration session could not be authorized." }),
    });
    return { document, provider, state: document.getMap("agreementState") };
  }, [initialAgreement.collaboration.documentKey]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ undoRedo: false }), Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Draft this provision..." }),
      ...agreementExtensions,
      Collaboration.configure({ document: collaboration.document, field: "agreementDocument" }),
      CollaborationCaret.configure({ provider: collaboration.provider, user: { name: user?.displayName || user?.name || "Collaborator", color: "#5e72e4" } }),
    ],
    editable: canEdit,
    onUpdate: ({ editor: current, transaction }) => {
      if (!synced || !hydratedRef.current || isChangeOrigin(transaction)) return;
      collaboration.document.transact(() => {
        collaboration.state.set("tiptapJson", current.getJSON());
        collaboration.state.set("revision", Number(collaboration.state.get("revision") || 0) + 1);
      }, "feradel-editor");
      markDirty();
    },
  }, [collaboration, canEdit, markDirty, synced]);

  useEffect(() => {
    const undoManager = collaborationHistoryManager(editor);
    if (!editor || !undoManager) return undefined;

    let disposed = false;
    const timers = new Set();
    const refresh = () => {
      if (disposed) return;
      setHistoryState({
        canUndo: undoManager.undoStack.length > 0,
        canRedo: undoManager.redoStack.length > 0,
      });
    };
    const schedule = (callback = refresh) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        if (!disposed) callback();
      }, 0);
      timers.add(timer);
    };
    const onStackChanged = () => schedule();
    const onStackPopped = () => schedule(() => {
      if (!hydratedRef.current || !synced || editor.isDestroyed) {
        refresh();
        return;
      }
      collaboration.document.transact(() => {
        collaboration.state.set("tiptapJson", editor.getJSON());
        collaboration.state.set("revision", Number(collaboration.state.get("revision") || 0) + 1);
      }, "feradel-history");
      markDirty();
      refresh();
    });

    undoManager.on("stack-item-added", onStackChanged);
    undoManager.on("stack-item-updated", onStackChanged);
    undoManager.on("stack-item-popped", onStackPopped);
    undoManager.on("stack-cleared", onStackChanged);
    refresh();

    return () => {
      disposed = true;
      timers.forEach((timer) => window.clearTimeout(timer));
      undoManager.off("stack-item-added", onStackChanged);
      undoManager.off("stack-item-updated", onStackChanged);
      undoManager.off("stack-item-popped", onStackPopped);
      undoManager.off("stack-cleared", onStackChanged);
    };
  }, [collaboration.document, collaboration.state, editor, markDirty, synced]);

  const tree = useMemo(() => numberedTree(agreement.provisionTree || [], agreement.numberingConfig || {}).items, [agreement.provisionTree, agreement.numberingConfig]);
  const activeProvision = tree.find((item) => item.id === activeProvisionId) || tree[0];
  const activeNode = editor?.getJSON()?.content?.find((node) => node.type === "agreementProvision" && node.attrs?.provisionId === activeProvision?.id);

  const syncStructure = useCallback((nextTree, nextDocument = editor?.getJSON(), extra = {}) => {
    if (!editor) return;
    const decorated = decorateDocument(nextDocument, nextTree, agreement.numberingConfig || {});
    collaboration.document.transact(() => {
      collaboration.state.set("provisionTree", nextTree);
      collaboration.state.set("tiptapJson", decorated);
      Object.entries(extra).forEach(([key, value]) => collaboration.state.set(key, value));
      editor.commands.setContent(decorated);
    }, "feradel-structure");
    setAgreement((current) => ({ ...current, provisionTree: nextTree, tiptapJson: decorated, ...extra }));
    markDirty();
  }, [agreement.numberingConfig, collaboration.document, collaboration.state, editor, markDirty]);

  useEffect(() => () => { collaboration.provider.destroy(); collaboration.document.destroy(); }, [collaboration]);

  useEffect(() => {
    const observeState = (event) => {
      const changed = event.keysChanged || new Set();
      if (["provisionTree", "numberingConfig", "signatureConfig", "writeLocked"].some((key) => changed.has(key))) {
        setAgreement((current) => ({
          ...current,
          provisionTree: collaboration.state.get("provisionTree") || current.provisionTree,
          numberingConfig: collaboration.state.get("numberingConfig") || current.numberingConfig,
          signatureConfig: collaboration.state.get("signatureConfig") || current.signatureConfig,
          collaboration: changed.has("writeLocked") ? { ...current.collaboration, readOnly: Boolean(collaboration.state.get("writeLocked")) } : current.collaboration,
        }));
      }
    };
    const variables = collaboration.document.getMap("agreementVariables");
    const deleted = collaboration.document.getMap("agreementTombstones");
    const observeVariables = () => setAgreement((current) => ({ ...current, variables: Object.values(variables.toJSON()) }));
    const observeDeleted = () => setTombstones(deleted.toJSON());
    collaboration.state.observe(observeState);
    variables.observe(observeVariables);
    deleted.observe(observeDeleted);
    observeDeleted();
    return () => { collaboration.state.unobserve(observeState); variables.unobserve(observeVariables); deleted.unobserve(observeDeleted); };
  }, [collaboration.document, collaboration.state]);

  useEffect(() => {
    if (!editor || !synced) return;
    const fragment = collaboration.document.getXmlFragment("agreementDocument");
    const stored = collaboration.state.get("tiptapJson") || agreement.tiptapJson;
    const sharedTree = collaboration.state.get("provisionTree") || agreement.provisionTree;
    const sharedNumbering = collaboration.state.get("numberingConfig") || agreement.numberingConfig;
    const sharedSignature = collaboration.state.get("signatureConfig") || agreement.signatureConfig;
    setAgreement((current) => ({ ...current, provisionTree: sharedTree, numberingConfig: sharedNumbering, signatureConfig: sharedSignature }));
    if (fragment.length === 0 && stored?.type === "doc") editor.commands.setContent(decorateDocument(stored, sharedTree, sharedNumbering));
    if (!hydratedRef.current) {
      collaborationHistoryManager(editor)?.clear();
      const timer = window.setTimeout(() => { hydratedRef.current = true; }, 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [agreement.numberingConfig, agreement.provisionTree, agreement.signatureConfig, agreement.tiptapJson, collaboration.document, collaboration.state, editor, synced]);

  const loadSupportingData = useCallback(async () => {
    const [contextResult, versionResult, auditResult, artifactResult] = await Promise.all([
      getAgreementContext(agreement.uuid),
      getAgreementVersions(agreement.uuid),
      getAgreementAudit(agreement.uuid),
      permissions.export ? getAgreementArtifacts(agreement.uuid) : Promise.resolve({ items: [] }),
    ]);
    setPreview(contextResult.preview);
    setMergeContext(contextResult.context || { aliases: {} });
    setValidation(contextResult.validation || {});
    setVersions(versionResult.items || []);
    setAudits(auditResult.items || []);
    setArtifacts(artifactResult.items || []);
  }, [agreement.uuid, permissions.export]);

  useEffect(() => { loadSupportingData().catch((error) => setMessage({ variant: "danger", text: errorMessage(error) })); }, [loadSupportingData]);

  useEffect(() => {
    if (!permissions.libraryView || !activeCategoryUuid) { setLibrary([]); return; }
    getAgreementLibrary({ matterUuid: matter.uuid, categoryUuid: activeCategoryUuid }).then((result) => setLibrary(result.items || [])).catch(() => setLibrary([]));
  }, [activeCategoryUuid, matter.uuid, permissions.libraryView]);

  const saveVersion = async (reason = "manual", description = "Manual editor save", override = {}) => {
    if (!editor) return;
    setBusy(true); setMessage(null);
    try {
      const currentTree = override.provisionTree || agreement.provisionTree;
      const currentDocument = decorateDocument(override.tiptapJson || editor.getJSON(), currentTree, agreement.numberingConfig);
      const result = await updateAgreement(agreement.uuid, {
        tiptapJson: currentDocument,
        provisionTree: currentTree,
        numberingConfig: agreement.numberingConfig,
        signatureConfig: agreement.signatureConfig,
        variables: override.variables || agreement.variables,
        snapshotReason: reason,
        snapshotDescription: description,
      });
      setAgreement((current) => ({ ...result.item, AgreementType: current.AgreementType }));
      onAgreementChange?.(result.item);
      await loadSupportingData();
      markDirty(false);
      setMessage({ variant: "success", text: "A recoverable agreement version was saved." });
      return true;
    } catch (error) {
      setMessage({ variant: "danger", text: errorMessage(error) });
      return false;
    }
    finally { setBusy(false); }
  };

  const savePendingChanges = async (reason = "manual", description = "Manual editor save") => {
    const rows = Object.entries(variableValues).map(([key, value]) => ({ key, label: key, value, sourceType: "manual-override", dataType: "string" }));
    let nextVariables = agreement.variables;
    if (rows.length) {
      nextVariables = [...(agreement.variables || []).filter((item) => !Object.prototype.hasOwnProperty.call(variableValues, item.key)), ...rows];
      collaboration.document.transact(() => {
        const sharedVariables = collaboration.document.getMap("agreementVariables");
        rows.forEach((row) => sharedVariables.set(row.key, row));
      }, "feradel-variables");
      setAgreement((current) => ({ ...current, variables: nextVariables }));
    }
    const saved = await saveVersion(reason, description, rows.length ? { variables: nextVariables } : {});
    if (saved) setVariableValues({});
    return saved;
  };

  useImperativeHandle(ref, () => ({
    hasPendingChanges: () => dirtyRef.current,
    isBusy: () => busy,
    savePendingChanges: () => savePendingChanges(),
  }));

  const selectProvision = (id) => {
    const item = tree.find((candidate) => candidate.id === id);
    setActiveProvisionId(id);
    if (item?.categoryId || item?.categoryUuid) setActiveCategoryUuid(item.categoryId || item.categoryUuid);
    window.setTimeout(() => document.querySelector(`[data-agreement-provision="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  };

  const addProvision = (category, content = [{ type: "paragraph", content: [{ type: "text", text: "Draft provision text." }] }], source = {}) => {
    const id = uuidv4();
    const nextTree = [...agreement.provisionTree, {
      id, parentId: null, categoryId: category.categoryUuid || category.categoryId || category.uuid,
      title: category.name || source.name || "Additional Provision", sortOrder: (agreement.provisionTree.length + 1) * 10,
      level: 0, numberingStyle: "decimal", status: "draft", sourceTemplateUuid: source.uuid || null,
      sourceTemplateVersionUuid: source.Version?.uuid || null,
    }];
    const nextDocument = { ...editor.getJSON(), content: [...(editor.getJSON().content || []), { type: "agreementProvision", attrs: { provisionId: id }, content }] };
    syncStructure(nextTree, nextDocument);
    setActiveProvisionId(id); setActiveCategoryUuid(category.categoryUuid || category.categoryId || category.uuid);
  };

  const applyProvisionContent = (content, source = {}) => {
    if (!activeProvision) { addProvision({ categoryUuid: activeCategoryUuid, name: source.name }, content, source); return null; }
    const nextDocument = { ...editor.getJSON(), content: editor.getJSON().content.map((node) => node.type === "agreementProvision" && node.attrs?.provisionId === activeProvision.id ? { ...node, content } : node) };
    const nextTree = agreement.provisionTree.map((item) => item.id === activeProvision.id ? { ...item, sourceTemplateUuid: source.uuid || item.sourceTemplateUuid, sourceTemplateVersionUuid: source.Version?.uuid || item.sourceTemplateVersionUuid, status: "accepted" } : item);
    syncStructure(nextTree, nextDocument);
    return { provisionTree: nextTree, tiptapJson: decorateDocument(nextDocument, nextTree, agreement.numberingConfig) };
  };

  const changeNesting = (id, direction) => {
    const ordered = tree;
    const index = ordered.findIndex((item) => item.id === id);
    const current = ordered[index];
    if (!current) return;
    let parentId = current.parentId;
    if (direction > 0 && index > 0) parentId = ordered[index - 1].id;
    if (direction < 0 && current.parentId) parentId = ordered.find((item) => item.id === current.parentId)?.parentId || null;
    const nextTree = agreement.provisionTree.map((item) => item.id === id ? { ...item, parentId } : item);
    syncStructure(nextTree);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ordered = [...tree];
    const from = ordered.findIndex((item) => item.id === active.id);
    const to = ordered.findIndex((item) => item.id === over.id);
    const [moved] = ordered.splice(from, 1); ordered.splice(to, 0, moved);
    const nextTree = agreement.provisionTree.map((item) => {
      const position = ordered.findIndex((candidate) => candidate.id === item.id);
      return { ...item, sortOrder: (position + 1) * 10 };
    });
    syncStructure(nextTree);
  };

  const removeProvision = async (id) => {
    const provision = agreement.provisionTree.find((item) => item.id === id);
    const node = editor.getJSON().content.find((item) => item.type === "agreementProvision" && item.attrs?.provisionId === id);
    if (!provision || !node) return;
    const deletedAt = new Date().toISOString();
    const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    collaboration.document.transact(() => collaboration.document.getMap("agreementTombstones").set(id, { provision, node, deletedAt, expiresAt, deletedByUuid: user?.uuid || null }), "feradel-delete-provision");
    const nextTree = agreement.provisionTree.filter((item) => item.id !== id).map((item) => item.parentId === id ? { ...item, parentId: provision.parentId || null } : item);
    const nextDocument = { ...editor.getJSON(), content: editor.getJSON().content.filter((item) => !(item.type === "agreementProvision" && item.attrs?.provisionId === id)) };
    syncStructure(nextTree, nextDocument);
    if (activeProvisionId === id) setActiveProvisionId(nextTree[0]?.id || null);
    await saveVersion("provision-removed", `Removed provision: ${provision.title}`, { provisionTree: nextTree, tiptapJson: nextDocument });
  };

  const restoreProvision = async (id) => {
    const tombstone = tombstones[id];
    if (!tombstone?.provision || !tombstone?.node) return;
    const restored = { ...tombstone, restoredAt: new Date().toISOString(), restoredByUuid: user?.uuid || null };
    collaboration.document.transact(() => collaboration.document.getMap("agreementTombstones").set(id, restored), "feradel-restore-provision");
    const nextTree = [...agreement.provisionTree, { ...tombstone.provision, sortOrder: (agreement.provisionTree.length + 1) * 10 }];
    const nextDocument = { ...editor.getJSON(), content: [...editor.getJSON().content, tombstone.node] };
    syncStructure(nextTree, nextDocument);
    setActiveProvisionId(id);
    await saveVersion("provision-restored", `Restored provision: ${tombstone.provision.title}`, { provisionTree: nextTree, tiptapJson: nextDocument });
  };

  const runAi = async () => {
    if (!activeNode || !aiInstruction.trim()) return;
    setBusy(true); setMessage(null);
    try {
      const result = await customizeAgreementProvision(agreement.uuid, {
        provisionUuid: activeProvision.id, category: activeProvision.title,
        template: templateText(activeNode).trim(), instruction: aiInstruction, draftingControls,
      });
      setAiOptions(result.options || []);
      setAiHistory((history) => [{ instruction: aiInstruction, options: result.options || [], createdAt: new Date().toISOString() }, ...history]);
    } catch (error) { setMessage({ variant: "danger", text: errorMessage(error) }); }
    finally { setBusy(false); }
  };

  const acceptAi = async (option) => {
    const accepted = applyProvisionContent(option.tiptapJson.content, { name: option.title });
    setAiOptions([]);
    await saveVersion("ai-customized", `Accepted AI alternative: ${option.title}`, accepted || {});
  };

  const saveAiToLibrary = async (option) => {
    setBusy(true);
    try {
      await addAgreementLibraryProvision({
        name: option.title, description: option.summary, categoryUuid: activeCategoryUuid,
        tiptapJson: option.tiptapJson, normalizedTemplate: option.template, source: "ai", scope: "private",
        matterUuid: matter.uuid, agreementTypes: [agreement.AgreementType?.slug || agreement.AgreementType?.name || "agreement"], jurisdictions: [agreement.jurisdiction],
        tags: option.draftingTags, proposedVariables: option.proposedVariables, warnings: option.warnings,
      });
      setMessage({ variant: "success", text: "The AI provision was saved as an unapproved library draft." });
    } catch (error) { setMessage({ variant: "danger", text: errorMessage(error) }); }
    finally { setBusy(false); }
  };

  const saveVariables = async () => {
    await savePendingChanges("variables-updated", "Resolved agreement-specific variables");
  };

  const setSignatureMode = (selection) => {
    const config = { ...(agreement.signatureConfig || {}), mode: selection.value, signers: selection.value === "none" ? [] : (agreement.signatureConfig?.signers?.length ? agreement.signatureConfig.signers : ["plaintiff", "defendant"]), notary: { ...(agreement.signatureConfig?.notary || {}), enabled: selection.value === "signature-and-notary", jurisdiction: agreement.jurisdiction } };
    collaboration.document.transact(() => collaboration.state.set("signatureConfig", config), "feradel-signature");
    setAgreement((current) => ({ ...current, signatureConfig: config }));
    markDirty();
  };

  const finalize = async () => {
    setShowFinalize(false);
    setMessage(null);
    const checkpointSaved = await savePendingChanges("pre-finalization", "Pre-finalization checkpoint");
    if (!checkpointSaved) return;
    setBusy(true); setMessage(null);
    try {
      const result = await finalizeAgreement(agreement.uuid);
      setAgreement((current) => ({ ...result.item, AgreementType: current.AgreementType }));
      onAgreementChange?.(result.item);
      await downloadAgreementArtifact(agreement.uuid, result.artifact);
      setMessage({ variant: "success", text: "The agreement was finalized and the editable DOCX was generated." });
    } catch (error) { setMessage({ variant: "danger", text: errorMessage(error) }); }
    finally { setBusy(false); }
  };

  const switchMode = async (nextMode) => {
    setMode(nextMode);
    if (nextMode === "preview") {
      try { const result = await getAgreementContext(agreement.uuid); setPreview(result.preview); setMergeContext(result.context || { aliases: {} }); setValidation(result.validation || {}); } catch (error) { setMessage({ variant: "danger", text: errorMessage(error) }); }
    }
  };

  const insertReference = () => {
    if (!referenceTarget || !editor) return;
    const target = tree.find((item) => item.id === referenceTarget.value);
    editor.chain().focus().insertProvisionReference({ targetProvisionId: target.id, referenceStyle: referenceStyle.value, includeTitle: false, resolved: true, resolvedText: referenceText(target, referenceStyle.value) }).run();
  };

  const mergeOptions = useMemo(() => Object.keys(mergeContext.aliases || {}).map((key) => ({ value: key, label: key })), [mergeContext.aliases]);
  const referenceOptions = tree.filter((item) => item.id !== activeProvisionId).map((item) => ({ value: item.id, label: `${item.displayNumber} ${item.title}` }));
  const signatureMode = agreement.signatureConfig?.mode || "signature-only";

  return (
    <div className="agreement-workspace">
      <div className="agreement-workspace-header">
        <div><h4>{agreement.title}</h4><Badge bg={agreement.status === "finalized" ? "success" : "secondary"}>{agreement.status}</Badge></div>
        <div className="agreement-header-actions"><span className={`agreement-connection is-${connection}`}>{dirty ? "Synced · version not saved" : synced ? "Saved & synced" : connection}</span><ButtonGroup size="sm"><Button variant={mode === "template" ? "primary" : "outline-primary"} onClick={() => switchMode("template")}>Template</Button><Button variant={mode === "preview" ? "primary" : "outline-primary"} onClick={() => switchMode("preview")}>Preview</Button></ButtonGroup>{canEdit ? <Button size="sm" variant="outline-primary" onClick={() => savePendingChanges()} disabled={busy}>Save version</Button> : null}{permissions.finalize && canEdit ? <Button size="sm" onClick={() => setShowFinalize(true)} disabled={busy}>Finalize</Button> : null}</div>
      </div>
      {message ? <Alert variant={message.variant} dismissible onClose={() => setMessage(null)}>{message.text}</Alert> : null}
      <div className="agreement-grid">
        <aside className="agreement-sidebar">
          <div className="agreement-panel-title"><span>Provision plan</span><Badge bg="light" text="dark">{tree.length}</Badge></div>
          <DndContext sensors={sensors} onDragEnd={onDragEnd}><SortableContext items={tree.map((item) => item.id)} strategy={verticalListSortingStrategy}>{tree.map((item) => <SortableProvision key={item.id} item={item} active={item.id === activeProvisionId} onSelect={selectProvision} onIndent={(id) => changeNesting(id, 1)} onOutdent={(id) => changeNesting(id, -1)} onRemove={removeProvision} readOnly={!canEdit} />)}</SortableContext></DndContext>
          {canEdit && plan.length ? <div className="agreement-plan-add"><Form.Label>Add provision</Form.Label><Select value={plan.find((item) => (item.categoryUuid || item.categoryId) === activeCategoryUuid) ? { value: activeCategoryUuid, label: plan.find((item) => (item.categoryUuid || item.categoryId) === activeCategoryUuid)?.name } : null} options={plan.map((item) => ({ value: item.categoryUuid || item.categoryId, label: `${item.name || "Provision"}${item.required ? " (required)" : ""}`, item }))} onChange={(option) => setActiveCategoryUuid(option.value)} placeholder="Choose a category"/><Button size="sm" variant="outline-primary" onClick={() => { const category = plan.find((item) => (item.categoryUuid || item.categoryId) === activeCategoryUuid); if (category) addProvision(category); }}>Add blank</Button></div> : null}
        </aside>

        <main className="agreement-editor-column">
          {mode === "template" ? <>
            {canEdit ? (
              <div className="agreement-toolbar" role="toolbar" aria-label="Agreement formatting">
                <div className="agreement-toolbar-row">
                  <ButtonGroup size="sm" aria-label="History">
                    <AgreementToolbarButton disabled={!historyState.canUndo} title="Undo (Ctrl+Z)" onClick={() => editor?.chain().focus().undo().run()}>↶</AgreementToolbarButton>
                    <AgreementToolbarButton disabled={!historyState.canRedo} title="Redo (Ctrl+Y)" onClick={() => editor?.chain().focus().redo().run()}>↷</AgreementToolbarButton>
                  </ButtonGroup>
                  <span className="agreement-toolbar-divider" />
                  <ButtonGroup size="sm" aria-label="Paragraph style">
                    <AgreementToolbarButton active={editor?.isActive("paragraph")} title="Normal paragraph" onClick={() => editor?.chain().focus().setParagraph().run()}>¶</AgreementToolbarButton>
                    {[1, 2, 3].map((level) => (
                      <AgreementToolbarButton key={level} active={editor?.isActive("heading", { level })} title={`Heading ${level}`} onClick={() => editor?.chain().focus().toggleHeading({ level }).run()}>
                        H{level}
                      </AgreementToolbarButton>
                    ))}
                  </ButtonGroup>
                  <span className="agreement-toolbar-divider" />
                  <ButtonGroup size="sm" aria-label="Text formatting">
                    <AgreementToolbarButton active={editor?.isActive("bold")} title="Bold (Ctrl+B)" onClick={() => editor?.chain().focus().toggleBold().run()}><strong>B</strong></AgreementToolbarButton>
                    <AgreementToolbarButton active={editor?.isActive("italic")} title="Italic (Ctrl+I)" onClick={() => editor?.chain().focus().toggleItalic().run()}><em>I</em></AgreementToolbarButton>
                    <AgreementToolbarButton active={editor?.isActive("underline")} title="Underline (Ctrl+U)" onClick={() => editor?.chain().focus().toggleUnderline().run()}><u>U</u></AgreementToolbarButton>
                    <AgreementToolbarButton active={editor?.isActive("strike")} title="Strikethrough" onClick={() => editor?.chain().focus().toggleStrike().run()}><s>S</s></AgreementToolbarButton>
                    <AgreementToolbarButton active={editor?.isActive("definedTerm")} title="Defined term (small caps)" onClick={() => editor?.chain().focus().toggleDefinedTerm({ term: editor?.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to) }).run()}>SC</AgreementToolbarButton>
                    <AgreementToolbarButton title="Clear formatting" onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().unsetTextAlign().run()}>Clear</AgreementToolbarButton>
                  </ButtonGroup>
                  <span className="agreement-toolbar-divider" />
                  <ButtonGroup size="sm" aria-label="Lists and quotations">
                    <AgreementToolbarButton active={editor?.isActive("bulletList")} title="Bulleted list" onClick={() => editor?.chain().focus().toggleBulletList().run()}>• List</AgreementToolbarButton>
                    <AgreementToolbarButton active={editor?.isActive("orderedList")} title="Numbered list" onClick={() => editor?.chain().focus().toggleOrderedList().run()}>1. List</AgreementToolbarButton>
                    <AgreementToolbarButton active={editor?.isActive("blockquote")} title="Block quote" onClick={() => editor?.chain().focus().toggleBlockquote().run()}>“ ”</AgreementToolbarButton>
                  </ButtonGroup>
                  <span className="agreement-toolbar-divider" />
                  <ButtonGroup size="sm" aria-label="Alignment">
                    <AgreementToolbarButton active={editor?.isActive({ textAlign: "left" })} title="Align left" onClick={() => editor?.chain().focus().setTextAlign("left").run()}>≡</AgreementToolbarButton>
                    <AgreementToolbarButton active={editor?.isActive({ textAlign: "center" })} title="Center" onClick={() => editor?.chain().focus().setTextAlign("center").run()}>≣</AgreementToolbarButton>
                    <AgreementToolbarButton active={editor?.isActive({ textAlign: "right" })} title="Align right" onClick={() => editor?.chain().focus().setTextAlign("right").run()}>≡</AgreementToolbarButton>
                    <AgreementToolbarButton active={editor?.isActive({ textAlign: "justify" })} title="Justify" onClick={() => editor?.chain().focus().setTextAlign("justify").run()}>☰</AgreementToolbarButton>
                  </ButtonGroup>
                </div>
                <div className="agreement-toolbar-row agreement-toolbar-insert-row">
                  <span className="agreement-toolbar-label">Insert</span>
                  <Select className="agreement-toolbar-select" value={mergeField} options={mergeOptions} onChange={setMergeField} placeholder="Merge field" aria-label="Merge field"/>
                  <Button size="sm" variant="light" disabled={!mergeField} onClick={() => editor?.chain().focus().insertMergeField({ key: mergeField.value, label: mergeField.label, dataType: "string", sourceType: "matter-context", resolved: false }).run()}>Merge field</Button>
                  <Select className="agreement-toolbar-select is-wide" value={referenceTarget} options={referenceOptions} onChange={setReferenceTarget} placeholder="Reference provision" aria-label="Reference provision"/>
                  <Select className="agreement-toolbar-select" value={referenceStyle} options={REFERENCE_STYLES.map((value) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }))} onChange={setReferenceStyle} aria-label="Reference style"/>
                  <Button size="sm" variant="light" disabled={!referenceTarget} onClick={insertReference}>Cross-reference</Button>
                  <Button size="sm" variant="light" onClick={() => editor?.chain().focus().setHorizontalRule().run()}>Horizontal line</Button>
                  <Button size="sm" variant="light" onClick={() => editor?.chain().focus().setPageBreak().run()}>Page break</Button>
                </div>
              </div>
            ) : null}
            <EditorContent editor={editor} className="agreement-paper" />
          </> : <Preview content={preview}/>} 
        </main>

        <aside className="agreement-inspector">
          {busy ? <div className="agreement-busy"><Spinner size="sm"/> Working…</div> : null}
          <Card body><div className="agreement-panel-title">Current provision</div><strong>{activeProvision?.displayNumber} {activeProvision?.title}</strong><small className="text-muted d-block mt-1">{activeProvision?.status || "draft"}</small></Card>
          {permissions.libraryView ? <Card body><div className="agreement-panel-title">Provision library</div>{library.length ? library.map((item) => <div className="agreement-library-item" key={item.uuid}><div><strong>{item.name}</strong><small>{item.status} · {item.scope}</small></div><div><Button size="sm" variant="link" onClick={() => applyProvisionContent(json(item.Version?.tiptapJson, { content: [] }).content || [], item)}>Use</Button>{permissions.libraryApprove && item.status !== "approved" ? <Button size="sm" variant="link" onClick={async () => { await approveAgreementLibraryVersion(item.uuid, item.Version.uuid); setLibrary((items) => items.map((value) => value.uuid === item.uuid ? { ...value, status: "approved" } : value)); }}>Approve</Button> : null}</div></div>) : <small className="text-muted">No provisions in this category yet.</small>}</Card> : null}
          {permissions.ai && canEdit ? <Card body><div className="agreement-panel-title">AI customization</div><Form.Control as="textarea" rows={3} value={aiInstruction} onChange={(event) => setAiInstruction(event.target.value)} placeholder="Describe the change. Client values remain tokenized."/><div className="agreement-control-chips">{["neutral", "more concise", "more detailed", "formal legal style"].map((control) => <Button key={control} size="sm" variant={draftingControls.includes(control) ? "primary" : "outline-secondary"} onClick={() => setDraftingControls((items) => items.includes(control) ? items.filter((item) => item !== control) : [...items, control])}>{control}</Button>)}</div><Button size="sm" className="w-100" onClick={runAi} disabled={!aiInstruction.trim() || busy}>Generate 3 alternatives</Button>{aiHistory.length > 1 ? <div className="agreement-ai-history"><small>Previous runs</small>{aiHistory.slice(1).map((run, index) => <Button key={run.createdAt} size="sm" variant="link" onClick={() => { setAiInstruction(run.instruction); setAiOptions(run.options); }}>#{aiHistory.length - index - 1} {run.instruction.slice(0, 34)}</Button>)}</div> : null}{aiOptions.map((option, index) => <div className="agreement-ai-option" key={`${option.title}-${index}`}><Badge bg="light" text="dark">Option {index + 1}</Badge><strong>{option.title}</strong><p>{option.summary}</p><div className="agreement-ai-template">{option.template}</div>{option.warnings?.map((warning) => <small className="text-warning d-block" key={warning}>{warning}</small>)}<Button size="sm" onClick={() => acceptAi(option)}>Accept</Button>{permissions.libraryAdd ? <Button size="sm" variant="link" onClick={() => saveAiToLibrary(option)}>Save draft to library</Button> : null}</div>)}</Card> : null}
          <Card body><div className="agreement-panel-title">Signatures</div><Select value={[{ value: "none", label: "No signers" }, { value: "signature-only", label: "Signature only" }, { value: "signature-and-notary", label: "Signature + notary" }].find((item) => item.value === signatureMode)} options={[{ value: "none", label: "No signers" }, { value: "signature-only", label: "Signature only" }, { value: "signature-and-notary", label: "Signature + notary" }]} onChange={setSignatureMode} isDisabled={!canEdit}/></Card>
          {(validation.unresolvedFields?.length || validation.unresolvedReferences?.length) ? <Card body className="agreement-validation"><div className="agreement-panel-title">Needs attention</div>{validation.unresolvedFields?.map((key) => <div key={key}><Form.Label>{key}</Form.Label><Form.Control size="sm" value={variableValues[key] || ""} onChange={(event) => { setVariableValues((values) => ({ ...values, [key]: event.target.value })); markDirty(); }}/></div>)}{validation.unresolvedReferences?.map((item, index) => <small className="text-danger d-block" key={index}>Unresolved reference: {item.targetProvisionId || "missing target"}</small>)}{Object.keys(variableValues).length ? <Button size="sm" className="mt-2" onClick={saveVariables}>Save values</Button> : null}</Card> : null}
          <Card body><div className="agreement-panel-title">Version history</div>{versions.slice(0, 5).map((item) => <div className="agreement-timeline" key={item.uuid}><strong>v{item.version} · {item.reason}</strong><small>{new Date(item.createdAt).toLocaleString()}</small></div>)}{!versions.length ? <small className="text-muted">No versions.</small> : null}</Card>
          {permissions.export && artifacts.length ? <Card body><div className="agreement-panel-title">Exports</div>{artifacts.map((artifact) => <Button key={artifact.uuid} size="sm" variant="outline-primary" className="w-100 mb-2" onClick={() => downloadAgreementArtifact(agreement.uuid, artifact)}>Download {artifact.name}</Button>)}</Card> : null}
          {canEdit && Object.values(tombstones).some((item) => !item.restoredAt) ? <Card body><div className="agreement-panel-title">Deleted provisions</div>{Object.entries(tombstones).filter(([, item]) => !item.restoredAt).map(([id, item]) => <div className="agreement-library-item" key={id}><strong>{item.provision?.title || "Deleted provision"}</strong><Button size="sm" variant="link" onClick={() => restoreProvision(id)}>Restore</Button></div>)}</Card> : null}
          <Card body><div className="agreement-panel-title">Audit</div>{audits.slice(0, 5).map((item) => <div className="agreement-timeline" key={item.uuid}><strong>{item.summary}</strong><small>{new Date(item.createdAt).toLocaleString()}</small></div>)}</Card>
        </aside>
      </div>
      <Modal show={showFinalize} onHide={() => setShowFinalize(false)} centered>
        <Modal.Header closeButton><Modal.Title>Finalize agreement</Modal.Title></Modal.Header>
        <Modal.Body><p className="mb-2">This creates an immutable version and editable Word document.</p><small className="text-muted">The active collaborative draft will be checkpointed and locked for all connected users.</small></Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" onClick={() => setShowFinalize(false)}>Cancel</Button><Button onClick={finalize}>Finalize and generate DOCX</Button></Modal.Footer>
      </Modal>
    </div>
  );
});

const Agreements = forwardRef(function Agreements({ agreementUuid, matter, auth, permissions, onAgreementLoaded, onDirtyChange }, ref) {
  const workspaceRef = useRef(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useImperativeHandle(ref, () => ({
    hasPendingChanges: () => workspaceRef.current?.hasPendingChanges?.() || false,
    isBusy: () => loading || workspaceRef.current?.isBusy?.() || false,
    savePendingChanges: () => workspaceRef.current?.savePendingChanges?.() || Promise.resolve(false),
  }));

  useEffect(() => {
    let activeRequest = true;
    setLoading(true);
    setMessage(null);
    setActive(null);
    Promise.all([getAgreement(agreementUuid), getAgreementTypes()])
      .then(([result, typeResult]) => {
        if (!activeRequest) return;
        const item = {
          ...result.item,
          AgreementType: (typeResult.items || []).find((type) => type.uuid === result.item.agreementTypeUuid),
        };
        setActive(item);
        onAgreementLoaded?.(result.item);
      })
      .catch((error) => {
        if (activeRequest) setMessage({ variant: "danger", text: errorMessage(error) });
      })
      .finally(() => {
        if (activeRequest) setLoading(false);
      });
    return () => { activeRequest = false; };
  }, [agreementUuid, onAgreementLoaded]);

  if (loading) return <div className="agreement-loading"><Spinner animation="border" size="sm"/> Loading agreement…</div>;
  if (message) return <Alert variant={message.variant}>{message.text}</Alert>;
  if (!active) return <Alert variant="warning">The agreement could not be opened.</Alert>;
  return <AgreementWorkspace ref={workspaceRef} agreement={active} matter={matter} user={auth?.user} permissions={permissions} onAgreementChange={onAgreementLoaded} onDirtyChange={onDirtyChange}/>;
});

export default Agreements;
